import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './Amenities.css';

const Amenities = () => {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});
  const [typeFilter, setTypeFilter] = useState('');
  const navigate = useNavigate();

  const types = [
    { value: '', label: 'All Types' },
    { value: 'Hotel', label: 'Hotel' },
    { value: 'SPA', label: 'SPA' }
  ];

  useEffect(() => {
    fetchAmenities();
  }, [typeFilter]);

  const fetchAmenities = async () => {
    try {
      const params = typeFilter ? { type: typeFilter } : {};
      const response = await axios.get(`${API_BASE_URL}/admin/amenities`, { params });
      setAmenities(response.data.data || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch amenities');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this amenity?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/admin/amenities/${id}`);
      fetchAmenities();
    } catch (err) {
      setError('Failed to delete amenity');
    }
  };

  const handleToggleActive = async (amenityId, currentStatus) => {
    const newStatus = !currentStatus;
    setTogglingStatus(prev => ({ ...prev, [amenityId]: true }));
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/amenities/${amenityId}`, {
        isActive: newStatus
      });
      setAmenities(amenities.map(a =>
        a._id === amenityId ? { ...a, isActive: response.data.data.isActive } : a
      ));
    } catch (err) {
      setError('Failed to update amenity status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [amenityId]: false }));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="amenities-header">
            <h1>Amenities Management</h1>
            <Link to="/sy-admin/amenities/new" className="btn btn-primary">
              <span className="btn-icon">+</span>
              Add New Amenity
            </Link>
          </div>
          <div className="amenities-table-container">
            <div className="skeleton skeleton-text" style={{ width: '100%', height: '300px', borderRadius: '12px' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="amenities-header">
          <h1>Amenities Management</h1>
          <Link to="/sy-admin/amenities/new" className="btn btn-primary">
            <span className="btn-icon">+</span>
            Add New Amenity
          </Link>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="amenities-stats">
          <div className="stat-card">
            <h3>{amenities.length}</h3>
            <p>Total Amenities</p>
          </div>
          <div className="stat-card">
            <h3>{amenities.filter(a => a.isActive).length}</h3>
            <p>Active Amenities</p>
          </div>
          <div className="amenities-filter">
            <label>Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              {types.map(t => (
                <option key={t.value || 'all'} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {amenities.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✨</div>
            <h2>No amenities found</h2>
            <p>Add amenities for Hotel room types and Spa services. Choose type Hotel or SPA when adding.</p>
            <Link to="/sy-admin/amenities/new" className="btn btn-primary">
              Add Your First Amenity
            </Link>
          </div>
        ) : (
          <div className="amenities-table-container">
            <table className="amenities-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {amenities.map((amenity) => (
                  <tr key={amenity._id}>
                    <td>
                      <div className="amenity-name-cell">
                        <span className="amenity-name-text">
                          {amenity.icon && <span className="amenity-icon-inline">{amenity.icon}</span>}
                          {amenity.name}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge type-${amenity.type?.toLowerCase()}`}>
                        {amenity.type || '-'}
                      </span>
                    </td>
                    <td>
                      <span>{amenity.displayOrder}</span>
                    </td>
                    <td>
                      <div className="status-cell">
                        <label className="status-toggle-label">
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={amenity.isActive}
                              onChange={() => handleToggleActive(amenity._id, amenity.isActive)}
                              disabled={togglingStatus[amenity._id]}
                              className="toggle-input"
                            />
                            <span className={`toggle-slider ${togglingStatus[amenity._id] ? 'loading' : ''}`}></span>
                          </div>
                          <span className={`status-text ${amenity.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                            {amenity.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          onClick={() => navigate(`/sy-admin/amenities/edit/${amenity._id}`)}
                          className="btn btn-edit btn-icon-only"
                          title="Edit Amenity"
                        >
                          <span className="btn-icon">✏️</span>
                        </button>
                        <button
                          onClick={() => handleDelete(amenity._id)}
                          className="btn btn-delete btn-icon-only"
                          title="Delete Amenity"
                        >
                          <span className="btn-icon">🗑️</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Amenities;
